import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppNavigation } from '../../hooks';
import { ROUTES, CONTACT_INFO } from '../../constants';
import { fetchSettings } from '../../services/api';
import type { SocialLinks } from '../../types';
import './Footer.css';

/**
 * Footer component with navigation links
 * Uses React Router Links for SPA navigation instead of anchor tags
 * Social links are fetched from backend settings
 */
const Footer: React.FC = () => {
	const { t } = useTranslation();
	const { goToMenu, goToGallery, goToFAQ } = useAppNavigation();
	const [socialLinks, setSocialLinks] = useState<SocialLinks>({
		instagram: 'https://instagram.com/familyfriendshibachi',
		facebook: 'https://facebook.com/familyfriendshibachi',
		tiktok: 'https://tiktok.com/@familyfriendshibachi',
	});

	useEffect(() => {
		const loadSettings = async () => {
			try {
				const response = await fetchSettings();
				if (response.success && response.settings?.socialLinks) {
					setSocialLinks(response.settings.socialLinks);
				}
			} catch (error) {
				// Use default social links
			}
		};
		loadSettings();
	}, []);

	const currentYear = new Date().getFullYear();

	return (
		<footer className="footer" role="contentinfo">
			<div className="footer-container">
				<div className="footer-main">
					{/* Brand */}
					<div className="footer-brand">
						<img src="/logo.png" alt="Family Friends Hibachi" className="footer-logo" />
						<p>{t('footer.description')}</p>
						<div className="footer-social">
							<a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
								📷
							</a>
							<a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
								📘
							</a>
							<a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok">
								🎵
							</a>
						</div>
					</div>

					{/* Quick Links */}
					<div className="footer-column">
						<h4>{t('footer.quickLinks')}</h4>
						<ul>
							<li>
								<Link to={`${ROUTES.BOOK_NOW}#california`}>{t('nav.california')}</Link>
							</li>
							<li>
								<Link to={`${ROUTES.BOOK_NOW}#texas`}>{t('nav.texas')}</Link>
							</li>
							<li>
								<Link to={`${ROUTES.BOOK_NOW}#florida`}>{t('nav.florida')}</Link>
							</li>
							<li>
								<button onClick={goToMenu} type="button">{t('nav.menu')}</button>
							</li>
							<li>
								<button onClick={goToGallery} type="button">{t('gallery.title')}</button>
							</li>
							<li>
								<button onClick={goToFAQ} type="button">{t('nav.faq')}</button>
							</li>
							<li>
								<Link to={ROUTES.MY_BOOKING}>{t('nav.myBooking')}</Link>
							</li>
						</ul>
					</div>

					{/* Services */}
					<div className="footer-column">
						<h4>{t('contact.address')}</h4>
						<ul>
							<li>{t('booking.california')}</li>
							<li>{t('booking.texas')}</li>
							<li>{t('booking.florida')}</li>
						</ul>
					</div>

					{/* Contact */}
					<div className="footer-column footer-contact">
						<h4>{t('footer.contact')}</h4>
						<p>
							<span>📞</span>
							<a href={`tel:${CONTACT_INFO.phone}`}>{CONTACT_INFO.phone}</a>
						</p>
						<p>
							<span>📧</span>
							<a href={`mailto:${CONTACT_INFO.email}`}>{CONTACT_INFO.email}</a>
						</p>
					</div>
				</div>

				{/* Bottom */}
				<div className="footer-bottom">
					<p>© {currentYear} FAMILY FRIENDS HIBACHI. {t('footer.allRightsReserved', 'All Rights Reserved.')}</p>
					<div className="footer-legal">
						<Link to="/privacy">{t('footer.privacyPolicy', 'Privacy Policy')}</Link>
						<Link to="/terms">{t('footer.termsOfService', 'Terms of Service')}</Link>
					</div>
				</div>
			</div>
		</footer>
	);
};

export default Footer;
